import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../ui/Button';
import Select from '../ui/Select';
import AppIcon from '../AppIcon';
import { cn } from '../../utils/cn';

interface FilterOption {
    key?: any;
    header?: string;
    name?: string;
    id?: any;
}

interface FilterProps {
    users?: FilterOption[];
    users_?: FilterOption[];
    selectedUsers?: FilterOption[];
    onChangeUser?: (ev: any, props: any) => any;
    clearAllUsers?: any;
    dateFrom?: Date | string | number;
    dateTo?: Date | string | number;
    onDateChange?: (time: any, type: any) => any;
    loading?: boolean;
    fetchData?: () => any;
    exportReport?: (format: string) => any;
    setShowFilters?: () => any;
    showFilters?: boolean;
    countries?: FilterOption[];
    selectedCountries?: FilterOption[];
    onChangeCountry?: (ev: any, props: any) => any;
    clearAllCountries?: any;
    branches?: FilterOption[];
    selectedBranches?: FilterOption[];
    onChangeBranches?: (ev: any, props: any) => any;
    clearAllBranches?: any;
    departments?: FilterOption[];
    selectedDepartments?: FilterOption[];
    onChangeDepartments?: (ev: any, props: any) => any;
    clearAllDepartments?: any;
    searchMethod?: string;
    setSearchMethod?: (event: any) => any;
    projects?: FilterOption[];
    selectedProjects?: FilterOption[];
    onChangeProject?: (ev: any, props: any) => any;
    clearAllProjects?: any;
    layoutMode?: 'flat' | 'grouped';
    onLayoutModeChange?: (mode: 'flat' | 'grouped') => any;
}

const asDate = (val: any) => {
    try {
        return val instanceof Date ? val : new Date(val);
    } catch {
        return new Date();
    }
};

/* ─── Data shape helpers ─── */
const toSelectOptions = (items: FilterOption[]) =>
    items.map((opt) => ({
        value: String(opt.key ?? opt.id),
        label: opt.header ?? opt.name ?? '',
    }));

const toSelectedValues = (items: FilterOption[]) =>
    items.map((opt) => String(opt.key ?? opt.id));

const fromSelectedValues = (
    selectedValues: string[],
    rawOptions: FilterOption[],
) =>
    rawOptions.filter((opt) =>
        selectedValues.includes(String(opt.key ?? opt.id)),
    );

/* ─── Filter field component ─── */
function FilterField({
    label,
    options,
    selected,
    onChange,
    onClear,
    placeholder,
}: {
    label: string;
    options: FilterOption[];
    selected: FilterOption[];
    onChange?: (ev: any, props: any) => any;
    onClear?: () => void;
    placeholder: string;
}) {
    const selectOptions = toSelectOptions(options);
    const selectedValues = toSelectedValues(selected);

    const handleChange = (values: string[]) => {
        const mapped = fromSelectedValues(values, options);
        onChange?.(undefined, { value: mapped });
    };

    return (
        <div className="flex-1 min-w-[200px]">
            <span className="text-xs font-medium text-muted-foreground mb-0.5 block">
                {label}
            </span>
            <div className="flex items-center gap-1">
                <Select
                    className="flex-1"
                    multiple
                    searchable
                    clearable
                    options={selectOptions}
                    value={selectedValues}
                    onChange={handleChange}
                    placeholder={placeholder}
                />
                {selected.length > 0 && onClear && (
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0"
                        onClick={onClear}
                        aria-label={`Clear ${label}`}
                    >
                        <AppIcon name="X" size={14} />
                    </Button>
                )}
            </div>
        </div>
    );
}

export default function ReportsController(props: FilterProps) {
    const { t } = useTranslation();
    const [exporting, setExporting] = React.useState<null | 'xlsx' | 'pdf'>(null);
    const {
        users = [],
        users_ = [],
        selectedUsers = [],
        onChangeUser,
        clearAllUsers,
        dateFrom = new Date(),
        dateTo = new Date(),
        onDateChange,
        loading = false,
        fetchData,
        exportReport,
        setShowFilters,
        showFilters = false,
        countries = [],
        selectedCountries = [],
        onChangeCountry,
        clearAllCountries,
        branches = [],
        selectedBranches = [],
        onChangeBranches,
        clearAllBranches,
        departments = [],
        selectedDepartments = [],
        onChangeDepartments,
        clearAllDepartments,
        searchMethod = 'AND',
        setSearchMethod,
        projects = [],
        selectedProjects = [],
        onChangeProject,
        clearAllProjects,
        layoutMode,
        onLayoutModeChange,
    } = props;

    const handleExport = async (format: 'xlsx' | 'pdf') => {
        if (!exportReport || exporting) return;
        try {
            setExporting(format);
            await Promise.resolve(exportReport(format));
        } catch {
            // no-op
        } finally {
            setExporting(null);
        }
    };

    const handleDate = (
        e: React.ChangeEvent<HTMLInputElement>,
        type: 'from' | 'to',
    ) => {
        const val = e.target.value ? new Date(e.target.value) : new Date();
        onDateChange?.(val, type);
    };

    const usersOptions = (users_?.length ? users_ : users) as FilterOption[];

    const hasAdvancedFilters =
        countries.length > 0 ||
        branches.length > 0 ||
        departments.length > 0 ||
        projects.length > 0;

    return (
        <div className="w-full border border-border rounded-lg bg-card overflow-hidden">
            {/* Header bar */}
            <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                <div className="flex items-center gap-1.5">
                    <AppIcon name="SlidersHorizontal" size={16} className="text-primary" />
                    <span className="text-sm font-semibold text-foreground">
                        {t('Reports Controller')}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    {layoutMode && onLayoutModeChange && (
                        <div className="flex rounded-md border border-border overflow-hidden">
                            <button
                                type="button"
                                className={cn(
                                    'px-3 py-1 text-xs font-medium transition-colors',
                                    layoutMode === 'flat'
                                        ? 'bg-primary text-primary-foreground'
                                        : 'bg-card text-foreground hover:bg-muted',
                                )}
                                onClick={() => onLayoutModeChange('flat')}
                            >
                                {t('Flat')}
                            </button>
                            <button
                                type="button"
                                className={cn(
                                    'px-3 py-1 text-xs font-medium transition-colors border-l border-border',
                                    layoutMode === 'grouped'
                                        ? 'bg-primary text-primary-foreground'
                                        : 'bg-card text-foreground hover:bg-muted',
                                )}
                                onClick={() => onLayoutModeChange('grouped')}
                            >
                                {t('Grouped')}
                            </button>
                        </div>
                    )}
                    <Button
                        size="sm"
                        variant="default"
                        onClick={() => !loading && fetchData?.()}
                        disabled={loading}
                        loading={loading}
                    >
                        {loading ? t('Loading...') : t('Search')}
                    </Button>
                    {exportReport && (
                        <>
                            <Button
                                size="sm"
                                variant="outline"
                                iconName="Download"
                                iconSize={14}
                                disabled={loading || !!exporting}
                                loading={exporting === 'xlsx'}
                                onClick={() => handleExport('xlsx')}
                            >
                                {t('Excel')}
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                iconName="FileText"
                                iconSize={14}
                                disabled={loading || !!exporting}
                                loading={exporting === 'pdf'}
                                onClick={() => handleExport('pdf')}
                            >
                                {t('PDF')}
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {/* Quick filters: Users + Dates */}
            <div className="px-3 py-2">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {/* Users */}
                    <FilterField
                        label={t('Users')}
                        options={usersOptions}
                        selected={selectedUsers}
                        onChange={onChangeUser}
                        onClear={clearAllUsers}
                        placeholder={t('common:Select users')}
                    />

                    {/* From Date */}
                    <div className="flex-1 min-w-[200px]">
                        <span className="text-xs font-medium text-muted-foreground mb-0.5 block">
                            {t('common:From Date')}
                        </span>
                        <input
                            type="date"
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                            value={asDate(dateFrom).toISOString().slice(0, 10)}
                            onChange={(e) => handleDate(e, 'from')}
                        />
                    </div>

                    {/* To Date */}
                    <div className="flex-1 min-w-[200px]">
                        <span className="text-xs font-medium text-muted-foreground mb-0.5 block">
                            {t('common:To Date')}
                        </span>
                        <input
                            type="date"
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                            value={asDate(dateTo).toISOString().slice(0, 10)}
                            onChange={(e) => handleDate(e, 'to')}
                        />
                    </div>
                </div>

                {/* Toggle advanced filters */}
                {hasAdvancedFilters && (
                    <div className="flex justify-end mt-1">
                        <Button
                            variant="ghost"
                            size="sm"
                            iconName={showFilters ? 'Minus' : 'Plus'}
                            iconSize={14}
                            onClick={() => setShowFilters?.()}
                        >
                            {showFilters ? t('common:Hide filters') : t('common:More filters')}
                        </Button>
                    </div>
                )}
            </div>

            {/* Advanced filters */}
            {showFilters && hasAdvancedFilters && (
                <div className="px-3 py-2 border-t border-border">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {countries.length > 0 && (
                            <FilterField
                                label={t('Countries')}
                                options={countries}
                                selected={selectedCountries}
                                onChange={onChangeCountry}
                                onClear={clearAllCountries}
                                placeholder={t('common:Select countries')}
                            />
                        )}
                        {branches.length > 0 && (
                            <FilterField
                                label={t('Branches')}
                                options={branches}
                                selected={selectedBranches}
                                onChange={onChangeBranches}
                                onClear={clearAllBranches}
                                placeholder={t('common:Select branches')}
                            />
                        )}
                        {departments.length > 0 && (
                            <FilterField
                                label={t('Departments')}
                                options={departments}
                                selected={selectedDepartments}
                                onChange={onChangeDepartments}
                                onClear={clearAllDepartments}
                                placeholder={t('common:Select departments')}
                            />
                        )}
                        {projects.length > 0 && (
                            <FilterField
                                label={t('Projects')}
                                options={projects}
                                selected={selectedProjects}
                                onChange={onChangeProject}
                                onClear={clearAllProjects}
                                placeholder={t('common:Select projects')}
                            />
                        )}

                        {/* Search method */}
                        <div className="flex-1 min-w-[200px]">
                            <span className="text-xs font-medium text-muted-foreground mb-0.5 block">
                                {t('Search method')}
                            </span>
                            <div className="flex items-center gap-4 h-10">
                                <label className="flex items-center gap-2 cursor-pointer text-sm">
                                    <input
                                        type="radio"
                                        name="searchMethod"
                                        value="AND"
                                        checked={searchMethod === 'AND'}
                                        onChange={(e) => setSearchMethod?.(e)}
                                        className="h-4 w-4 rounded-full border border-input text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                                    />
                                    {t('AND')}
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer text-sm">
                                    <input
                                        type="radio"
                                        name="searchMethod"
                                        value="OR"
                                        checked={searchMethod === 'OR'}
                                        onChange={(e) => setSearchMethod?.(e)}
                                        className="h-4 w-4 rounded-full border border-input text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                                    />
                                    {t('OR')}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
